import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { useOperatorStore } from '@/core/stores/operator-store';
import type { OperatorApp } from '@/features/operator-selection/types/operator-types';
import { hasPendingRecord, isFlowDirty, useKioskFlowStore } from '../../stores/kiosk-flow-store';
import { OperatorCombobox } from '../molecules/OperatorCombobox';
import { DiscardChangesDialog } from '../molecules/DiscardChangesDialog';

export function OperatorFlowSelector() {
  const operators = useOperatorStore((s) => s.operators);
  const operatorsLoading = useOperatorStore((s) => s.isLoading);
  const operatorsError = useOperatorStore((s) => s.error);
  const loadOperators = useOperatorStore((s) => s.loadOperators);

  const selectedOperator = useKioskFlowStore((s) => s.selectedOperator);
  const selectOperator = useKioskFlowStore((s) => s.selectOperator);
  // Switching operators mid-publish would hide the result of the publish, and with a record
  // still pending it would drop the JSON that Retry record needs
  const isPublishing = useKioskFlowStore((s) => s.isPublishing);
  const pendingRecord = useKioskFlowStore(hasPendingRecord);
  // Operator picked while the flow had unsaved changes — switched to once the user discards them
  const [pendingOperator, setPendingOperator] = useState<OperatorApp | null>(null);

  const handleSelect = (operator: OperatorApp) => {
    if (operator.operatorId === selectedOperator?.operatorId) return;
    if (isFlowDirty(useKioskFlowStore.getState())) {
      setPendingOperator(operator);
      return;
    }
    selectOperator(operator);
  };

  useEffect(() => {
    if (operators.length === 0) loadOperators();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Operator</CardTitle>
        <CardDescription>Choose the operator whose kiosk flow you want to work on</CardDescription>
      </CardHeader>
      <CardContent className="max-w-md space-y-1">
        <label className="block text-xs font-medium text-gray-600">
          Operator <span className="text-red-500">*</span>
        </label>
        <OperatorCombobox
          operators={operators}
          selected={selectedOperator}
          onSelect={handleSelect}
          isLoading={operatorsLoading}
          disabled={isPublishing || pendingRecord}
        />
        {pendingRecord && (
          <p className="text-xs text-amber-600">Retry recording the version or dismiss it before switching operators</p>
        )}
        {operatorsError && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <span>{operatorsError}</span>
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => loadOperators()}>
              Retry
            </Button>
          </div>
        )}
      </CardContent>
      <DiscardChangesDialog
        open={pendingOperator !== null}
        onOpenChange={(open) => !open && setPendingOperator(null)}
        onConfirm={() => {
          if (pendingOperator) selectOperator(pendingOperator);
          setPendingOperator(null);
        }}
      />
    </Card>
  );
}
